import { useState } from "react";

interface AvatarProps {
  name: string;
  url?: string | null;
  size?: number;
  /** A thin ring. Used where avatars sit on top of each other and need separating. */
  ringed?: boolean;
}

/**
 * A face, or the initial standing in for one.
 *
 * The initial is not a fallback so much as the normal case: Google supplies a picture for
 * most people and no picture for some, and a member who joined without one should not look
 * like a broken image. It is also what shows while a picture is loading and after one
 * 404s — Google's avatar URLs do go stale, which is half the reason uploads exist.
 */
export function Avatar({ name, url, size = 28, ringed = false }: AvatarProps) {
  const [failed, setFailed] = useState(false);
  const initial = name.trim().charAt(0).toUpperCase() || "?";

  return (
    <span
      // `self-center` is load-bearing, and not for the reason it looks like.
      //
      // A row of the leaderboard is `flex items-baseline`, so every child is aligned on
      // its baseline. This span's baseline depends on what is inside it: with an initial
      // it has real text and sits on the letter's baseline, but with an <img> there is no
      // in-flow text and CSS synthesises the baseline at the bottom margin edge instead.
      // That is 8-10px lower, and baseline-aligning a box whose baseline is lower pushes
      // the whole flex line down with it.
      //
      // The visible result was a board whose rows were 65.5px for members with a picture
      // and 55.5px for members without -- which nobody saw locally, because seeded members
      // have no avatar and every row was the short kind.
      //
      // Opting out of baseline alignment fixes it at the source. An avatar wants to be
      // centred against the row anyway; it was never sitting on that baseline on purpose.
      className="font-display grid shrink-0 place-items-center self-center overflow-hidden rounded-full"
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.42),
        fontWeight: 800,
        background: "var(--color-raised)",
        color: "var(--color-ink-muted)",
        border: ringed ? "2px solid var(--color-ground)" : undefined,
      }}
      aria-hidden
    >
      {url && !failed ? (
        <img
          src={url}
          alt=""
          className="size-full object-cover"
          loading="lazy"
          onError={() => setFailed(true)}
        />
      ) : (
        initial
      )}
    </span>
  );
}

/**
 * A side of a match, overlapped the way a team is written on a scoresheet. Overlapping
 * rather than spacing keeps a doubles pair readable as one unit at row height.
 */
export function AvatarPair({
  people,
  size = 28,
}: {
  people: { name: string; url?: string | null }[];
  size?: number;
}) {
  return (
    // Same reasoning as Avatar above: a flex container takes its baseline from its first
    // item, so a pair whose first face is a picture would hand a bottom-edge baseline up to
    // whatever row it sits in.
    <span className="flex shrink-0 items-center self-center">
      {people.map((person, i) => (
        <span key={`${person.name}-${i}`} style={{ marginLeft: i === 0 ? 0 : -size * 0.3 }}>
          <Avatar name={person.name} url={person.url} size={size} ringed={people.length > 1} />
        </span>
      ))}
    </span>
  );
}
